import React, { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Button } from '@components/button';
import { InputLabel } from '@components/inputLabel';
import {
  Box,
  FormControl,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { CaretDown } from '@assets/svgs';
import { MenuItem } from '@components/menu';
import { Checkbox } from '@components/checkbox';
import { InputLabelContainer } from '@components/container/InputLabelContainer';
import * as styles from '@components/select/Select.styles';
import composeTheme from '@theme/theme';
import { AdditionalInfoLinkProps } from '@components/container/AdditionalInfoLink';

const placeholderValue = '';

export interface Item {
  key?: string;
  value: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
  sx?: SxProps | ((theme: Theme) => SxProps);
  secondaryLabel?: string;
}

type SelectFieldExtendedProps = SelectProps<string> & {
  inputLabel?: string;
  inputLabelIcon?: ReactNode;
  tooltipTitle?: ReactNode;
  inputLabelId?: string;
  isFloatingLabel?: boolean;
  externalIcon?: ReactNode;
  isAddItem?: boolean;
  addItemProps?: Item[];
  addNewItemAction?: () => void;
  items?: Item[];
  helperLabel?: string;
  onChange?: (event: SelectChangeEvent, newValue?: string) => void;
  onChangeSelect?: (eventValue: string) => void;
  fullWidth?: boolean;
  additionalInfoLink?: AdditionalInfoLinkProps;
  placeholder?: string;
  deprecated?: boolean | null;
  requiredBeforeBind?: boolean;
};

export function Select(props: SelectFieldExtendedProps) {
  const {
    id,
    disabled,
    error,
    helperLabel,
    isFloatingLabel,
    fullWidth,
    inputLabel,
    inputLabelId = isFloatingLabel && inputLabel ? inputLabel.toLowerCase().replace(/\s/g, '') : '',
    inputLabelIcon,
    tooltipTitle,
    isAddItem,
    addItemProps,
    items,
    externalIcon,
    onChangeSelect,
    addNewItemAction,
    placeholder,
    sx,
    variant = 'outlined',
    value,
    required,
    multiple,
    defaultValue,
    onChange,
    additionalInfoLink,
    deprecated,
    requiredBeforeBind,
    ...other
  } = props;
  const { palette } = composeTheme('light');

  const iconRef = useRef<any>(null);

  const [muiValue, setMuiValue] = useState<number | number[] | string | string[] | undefined>(defaultValue ?? value);

  const handleOnChange = (event: SelectChangeEvent, newValue?: string) => {
    setMuiValue(event.target.value);
    onChange?.(event, newValue);
    onChangeSelect?.(event.target.value);
  };

  // A caller can define a real, selectable option whose value is the same empty-string sentinel this
  // component otherwise reserves for "nothing selected yet" (e.g. an explicit "None" choice). When that
  // happens, treat the field as never truly empty -- otherwise MUI's Select skips calling `renderValue`
  // for an empty-string value unless `displayEmpty` is set, so the real option's label never renders as
  // selected, and the auto-injected placeholder item below would collide with the caller's own item.
  const hasExplicitEmptyOption = items?.some((item) => item.value === placeholderValue);
  const hasOneItem = items?.length === 1;
  const isAutoSelected = !value && hasOneItem;

  useEffect(() => {
    if (isAutoSelected) {
      onChangeSelect?.(items[0].value);
    }
    // eslint-disable-next-line
  }, [isAutoSelected, value]);

  useEffect(() => {
    setMuiValue(value);
  }, [value]);

  const generatedId = useId();
  const fieldId = id ?? other.name ?? generatedId;
  const iconWidth = iconRef.current ? iconRef.current.offsetWidth : 0;

  return (
    <FormControl
      sx={[
        !isFloatingLabel && styles.staticLabel,
        fullWidth
          ? externalIcon
            ? {
                '&.MuiFormControl-root': {
                  width: '100%',
                  '& .MuiInputBase-root': {
                    width: `calc(100% - ${iconWidth}px)`,
                  },
                },
              }
            : styles.fullWidth
          : {},
        {
          height: '100%',
          justifyContent: 'flex-end',
        },
      ]}
    >
      <InputLabelContainer
        inputLabelProps={{
          children: !isFloatingLabel ? inputLabel : '',
          endIcon: inputLabelIcon,
          tooltipTitle,
          error,
          required,
          deprecated,
          requiredBeforeBind,
          htmlFor: `${fieldId}-select`,
          id: fieldId,
          disabled,
          shrink: false,
        }}
        helperLabelProps={{
          children: helperLabel,
          error,
          disabled,
        }}
        additionalInfoLink={additionalInfoLink}
      >
        {isFloatingLabel && (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <InputLabel
              id={inputLabelId}
              deprecated
              sx={[styles.floatingLabel, !!disabled && styles.isDisabled, !!value && styles.hasSelection]}
            >
              {inputLabel}
            </InputLabel>
          </Box>
        )}
        <MuiSelect
          id={`${fieldId}`}
          labelId={`${fieldId}-label`}
          aria-label={`Select ${inputLabel ?? 'field'}`}
          displayEmpty={!!placeholder || hasExplicitEmptyOption}
          notched={!!(isFloatingLabel && inputLabel)}
          variant={variant}
          disabled={disabled}
          error={error}
          onChange={(e, newValue) => {
            handleOnChange(e, (newValue as any)?.props?.children);
          }}
          inputProps={{
            id: `${fieldId}-select`,
          }}
          {...(!defaultValue && defaultValue !== '' ? { value: value || placeholderValue } : { defaultValue })} // NOTE 5/2, using defaultValue + value not recommended, this is to only use this if a value is given.
          IconComponent={CaretDown}
          label={isFloatingLabel && inputLabel}
          MenuProps={{
            PaperProps: { sx: { maxHeight: '300px' } },
          }}
          sx={[styles.selectStyles, ...(Array.isArray(sx) ? sx : [sx]), isFloatingLabel && styles.hasSelection]}
          multiple={multiple}
          renderValue={(selected) => {
            if (Array.isArray(selected)) {
              return selected
                ?.map((s) => items?.find((i) => i.value === s)?.label ?? null)
                .filter(Boolean)
                .join(', ');
            }

            const item = items?.find((i) => i.value?.toString() === selected) ?? null;
            if (!item) return null;

            return item.secondaryLabel ? (
              <Typography variant='bodySmall'>
                {item.label}
                <Box component='span' sx={{ color: 'text.subdued', ml: '4px' }}>
                  {item.secondaryLabel}
                </Box>
              </Typography>
            ) : (
              item.label
            );
          }}
          {...other}
        >
          {!multiple && !hasExplicitEmptyOption && (
            <MenuItem
              disabled={!!placeholder}
              value={placeholderValue}
              sx={[
                { color: 'text.placeholder' },
                !isFloatingLabel && !!placeholder && styles.menuLabel,
                { pointerEvents: 'none' },
              ]}
            >
              <Box component='span' sx={{ color: 'text.placeholder' }}>
                {placeholder || inputLabel}
              </Box>
            </MenuItem>
          )}
          {items?.map((item: Item) =>
            multiple ? (
              <MenuItem
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                sx={{ display: item.hidden ? 'none' : 'flex', cursor: 'pointer', ...item.sx }}
              >
                <Checkbox
                  sxProps={{ height: 5, background: 'transparent' }}
                  checked={
                    Array.isArray(muiValue) && item.value
                      ? (muiValue as Array<string | number>).indexOf(item.value) > -1
                      : false
                  }
                />
                {item.label}
              </MenuItem>
            ) : (
              <MenuItem
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                sx={{ display: item.hidden ? 'none' : 'flex', color: palette.text.main }}
              >
                {item.label}{' '}
                {item.secondaryLabel && (
                  <Box component='span' sx={{ color: palette.text.subdued, ml: '4px' }}>
                    {item.secondaryLabel}
                  </Box>
                )}
              </MenuItem>
            ),
          )}
          {isAddItem &&
            addItemProps?.map((item) => (
              <MenuItem key={item.key}>
                <Button
                  variant='text'
                  onClick={addNewItemAction}
                  component='a'
                  sx={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    margin: '0 auto',
                  }}
                >
                  {item.label}
                </Button>
              </MenuItem>
            ))}
        </MuiSelect>
        <Box ref={iconRef}>{externalIcon}</Box>
      </InputLabelContainer>
    </FormControl>
  );
}
